import React, { useState, useEffect, useContext } from 'react';
import * as Sentry from '@sentry/react';

import { Input, Button, Card, CardHeader, CardBody, CardTitle, FormGroup, Label, Row, Col, UncontrolledTooltip, Table } from 'reactstrap';

import toast from 'react-hot-toast';

import { setting, member, guestDocument } from '../../api';
import UserContext from '../UserContext';
import { FaSpinner, FaInfo } from 'react-icons/fa';

import { SettingsClearancesType } from 'api/settings/clearances';
import { MemberDB, MemberHeaderDB } from 'api/members';
import { GuestDocumentDB } from 'api/guest-documents';
import { MemberDocumentDB } from 'api/members/document';
import { InvalidParametersException } from 'api/exceptions/InvalidParametersException';
import { useNavigate } from 'react-router-dom';
import { ForbiddenException } from 'api/exceptions/ForbiddenException';
import { GuestDocumentMemberSearch } from './partials/GuestDocumentMemberSearch';
import { ModalOverlay, ModalOverlayDelete } from 'components/Modal/Modal';

interface Props {}

export const GuestDocumentList = (props: Props) => {
	const { jwt } = useContext(UserContext);
	let navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(true);
	const [clearanceHeaders, setClearanceHeaders] = useState<Array<SettingsClearancesType>>([]);
	const [data, setData] = useState<Array<GuestDocumentDB>>([]);
	const [loadingError, setLoadingError] = useState(false);
	const [docIDPendingDelete, setDocIDPendingDelete] = useState<string | null>(null);

	useEffect(() => {
		const getContents = async () => {
			try {
				const clearanceData = await setting.clearances.get(jwt);
				setClearanceHeaders(clearanceData.types_list);

				const responseObj = await guestDocument.list(jwt);
				setData(responseObj);

				setIsLoading(false);
			} catch (error) {
				if (error instanceof ForbiddenException) {
					toast.error(error.message);
				} else {
					setLoadingError(true);
					Sentry.captureException(error);
					toast.error('Failed to load');
				}
			}
		};

		getContents();
	}, []);

	const handleViewDocumentClick = (docID: string | undefined) => {
		if (docID == null) {
			toast.error('Document ID is missing');
			return;
		}

		navigate(`/administrator/guest-documents/${docID}`);
	};

	const handleDeleteConfirm = async () => {
		if (docIDPendingDelete == null) {
			return;
		}

		try {
			await guestDocument.delete(jwt, docIDPendingDelete);
			setData(data.filter((row) => row.docID !== docIDPendingDelete));
			toast.success('Guest document deleted');
		} catch (error) {
			if (error instanceof ForbiddenException) {
				toast.error(error.message);
			} else {
				Sentry.captureException(error);
				toast.error('Failed to delete document');
			}
		}

		setDocIDPendingDelete(null);
	};

	return (
		<div className="content guest-documents-table">
			<Row>
				<Col md="12">
					<Card>
						<CardHeader>
							<CardTitle tag="h4">Guest Documents</CardTitle>
						</CardHeader>
						<CardBody>
							<Table responsive>
								<thead className="text-primary">
									<tr>
										<th>Type</th>
										<th>Uploaded Date</th>
										<th>Email Address</th>
										<th>Date of Birth</th>
										<th>First Name</th>
										<th>Last Name</th>
										<th className="text-right">Actions</th>
									</tr>
								</thead>
								{loadingError ? (
									<tbody>
										<tr>
											<td colSpan={7}>There was an error loading data.</td>
										</tr>
									</tbody>
								) : isLoading ? (
									<tbody>
										<tr>
											<td colSpan={7}>Loading...</td>
										</tr>
									</tbody>
								) : (
									<tbody>
										{data != null && data.length > 0 ? (
											data.map((row, key) => {
												return (
													<tr key={`guest-document-content-${row.docID}`}>
														<td>{clearanceHeaders.find((clearance) => clearance.uid === row.type)?.name}</td>
														<td>{`${`${row.uploaded_date.getMonth() + 1}`.padStart(
															2,
															'0',
														)}-${`${row.uploaded_date.getDate()}`.padStart(2, '0')}-${row.uploaded_date.getFullYear()}`}</td>
														<td>{row.emailAddress}</td>
														<td>{`${`${row.dateOfBirth.getMonth() + 1}`.padStart(2, '0')}-${`${row.dateOfBirth.getDate()}`.padStart(
															2,
															'0',
														)}-${row.dateOfBirth.getFullYear()}`}</td>
														<td>{row.firstName}</td>
														<td>{row.lastName}</td>
														<td className="text-right">
															<Button
																className="btn-icon"
																color={'secondary'}
																id={`guest-document-content-${key}-tooltip-like`}
																size="sm"
																type="button"
																onClick={() => handleViewDocumentClick(row.docID)}
															>
																<i className="fa fa-user" />
															</Button>
															<UncontrolledTooltip delay={0} target={`guest-document-content-${key}-tooltip-like`}>
																View Details
															</UncontrolledTooltip>
															<Button
																className="btn-icon"
																color={'danger'}
																id={`guest-document-content-${key}-tooltip-delete`}
																size="sm"
																type="button"
																onClick={() => setDocIDPendingDelete(row.docID ?? null)}
															>
																<i className="fa fa-trash" />
															</Button>
															<UncontrolledTooltip delay={0} target={`guest-document-content-${key}-tooltip-delete`}>
																Delete
															</UncontrolledTooltip>
														</td>
													</tr>
												);
											})
										) : (
											<tr>
												<td colSpan={7}>No members found.</td>
											</tr>
										)}
									</tbody>
								)}
							</Table>
						</CardBody>
					</Card>
				</Col>
			</Row>
			<ModalOverlayDelete
				show={docIDPendingDelete != null}
				title="Delete Guest Document?"
				primaryActionCallback={handleDeleteConfirm}
				secondaryActionCallback={() => setDocIDPendingDelete(null)}
			>
				<p>Are you sure you want to delete this guest document and its uploaded file? This action cannot be undone.</p>
			</ModalOverlayDelete>
		</div>
	);
};
